import ApexCharts from 'apexcharts';

const DARK_THEME = { mode: 'dark' };
const CHART_HEIGHT = 400;

const COLORS = [
    '#00e5ff',
    '#ff3d71',
    '#00ff88',
    '#ffa726',
    '#ab47bc',
    '#ffeb3b',
    '#26c6da',
    '#ec407a',
    '#7c4dff',
    '#64ffda'
];

/**
 * Build ApexCharts configuration for a time-based line chart.
 *
 * @param {Array} series - Chart series data
 * @returns {Object} ApexCharts options object
 */
function getChartOptions(series) {
    return {
        theme: DARK_THEME,

        chart: {
            type: 'line',
            height: CHART_HEIGHT,
            zoom: {
                enabled: true,
                type: 'x',
                autoScaleYaxis: true
            },
            redrawOnParentResize: true,
            background: 'transparent',
            toolbar: {
                show: true,
                offsetY: -5
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 600,
                dynamicAnimation: { speed: 350 }
            }
        },

        colors: COLORS,

        series,

        dataLabels: { enabled: false },

        markers: {
            size: 0,
            hover: {
                size: 7,
                sizeOffset: 3
            }
        },

        stroke: {
            width: 2,
            curve: 'smooth',
            lineCap: 'round'
        },

        xaxis: {
            type: 'datetime',
            labels: {
                datetimeUTC: false,
                  datetimeFormatter: {
                    year: 'yyyy',
                    month: 'MMM \'yy',
                    day: 'dd MMM',
                    hour: 'HH:mm'
                  },
                rotate: -45,
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            },
            axisTicks: {
                show: true,
                color: '#4b5563'
            },
            tooltip: { enabled: false }
        },

        yaxis: {
            title: {
                text: 'Points',
                style: {
                    color: '#d1d5db',
                    fontSize: '13px',
                    fontWeight: 600
                }
            },
            labels: {
                formatter: value => value.toFixed(0),
                style: {
                    colors: '#9ca3af',
                    fontSize: '12px',
                    fontWeight: 500
                }
            },
            axisBorder: {
                show: true,
                color: '#4b5563'
            }
        },

        tooltip: {
            theme: 'dark',
            x: { format: 'HH:mm dd/MM/yyyy' },
            style: { fontSize: '12px' },
            custom({ seriesIndex, dataPointIndex, w }) {
                const point = w.config.series[seriesIndex]?.data[dataPointIndex];
                if (!point) return '';

                const color = w.config.colors[seriesIndex];
                const date = new Date(point.x).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });

                return `
                    <div class="px-3 py-2 bg-gray-900 border border-gray-700 rounded-md"
                         style="border-left: 3px solid ${color}">
                        <div class="text-xs text-gray-400 mb-1">${date}</div>
                        <div class="flex items-center gap-2 mb-1">
                            <div class="w-2 h-2 rounded-full" style="background:${color}"></div>
                            <span class="font-medium text-white text-sm">
                                ${w.config.series[seriesIndex].name}
                            </span>
                        </div>
                        <div class="flex items-center gap-2">
                            <i class="fa-solid fa-star text-yellow-400 text-xs"></i>
                            <span class="text-sm font-semibold text-gray-200">
                                ${point.y} pts
                            </span>
                        </div>
                    </div>
                `;
            }
        },

        legend: {
            position: 'top',
            horizontalAlign: 'left',
            labels: { colors: '#d1d5db' },
            markers: {
                width: 14,
                height: 14,
                radius: 4
            },
            fontSize: '13px',
            fontWeight: 600
        },

        grid: {
            borderColor: '#374151',
            strokeDashArray: 3,
            padding: {
                top: 10,
                right: 20,
                bottom: 10,
                left: 10
            }
        },

        fill: {
            type: 'gradient',
            gradient: {
                shade: 'dark',
                type: 'vertical',
                shadeIntensity: 0.3,
                opacityFrom: 0.8,
                opacityTo: 0.5,
                stops: [0, 90, 100]
            }
        },

        states: {
            hover: {
                filter: { type: 'lighten', value: 0.15 }
            }
        }
    };
}

/**
 * Alpine-compatible ApexCharts manager.
 *
 * @param {HTMLElement} el - Chart container element
 * @param {Array} initialSeries - Initial chart series
 * @returns {Object} Chart controller
 */
export default function chartManager(el, initialSeries = []) {
    return {
        chart: null,
        series: initialSeries,

        init() {
            this.chart = new ApexCharts(el, getChartOptions(this.series));
            this.chart.render();

            this.$watch(
                'series',
                series => this.chart?.updateSeries(series, true),
                { deep: true }
            );

            requestAnimationFrame(() =>
                window.dispatchEvent(new Event('resize'))
            );
        },

        destroy() {
            this.chart?.destroy();
            this.chart = null;
        }
    };
}
